import { CSSProperties, KeyboardEvent, ReactNode, useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { LucideIcon } from 'lucide-react'

export interface OpcionMenu {
  clave: string
  texto: string
  Icono?: LucideIcon
  /** Las opciones con distinto `grupo` se separan (dcc-menu__group). */
  grupo?: number
  /** Para menús de selección (tema, filtro): marca la opción vigente. */
  marcada?: boolean
  /** Una acción que saca o marca el bien (no ubicado, baja): en rojo. */
  peligro?: boolean
}

/**
 * Menú desplegable del kit (`dcc-menu`). El kit trae el comportamiento en su bundle,
 * pero en React ese JS no se carga (enlaza manejadores sobre el DOM que administra
 * React), así que va acá: abre con clic, Enter o flechas; se recorre con flechas,
 * Home y End; cierra con Escape —devolviendo el foco al disparador—, con Tab, con
 * un clic fuera o al elegir.
 *
 * `flotante`: la lista se posiciona fija respecto de la ventana, hacia abajo o hacia
 * arriba según el espacio que haya. Es para menús dentro de un contenedor con scroll
 * (una fila de tabla), que de otro modo recortaría la lista. Se cierra al desplazar.
 */
export function Menu({
  disparador, etiqueta, claseDisparador = '', className = '', antes, opciones, onElegir, hacia = 'abajo', alineado = 'inicio', flotante = false,
}: {
  disparador: ReactNode
  /** Nombre accesible del disparador (obligatorio si el disparador es solo un ícono). */
  etiqueta: string
  claseDisparador?: string
  /** El kit espera que `dcc-menu` conviva con `dcc-sidebar__user` o `__action` en el mismo elemento. */
  className?: string
  /** Lo que va dentro del contenedor antes del disparador (avatar, nombre). */
  antes?: ReactNode
  opciones: OpcionMenu[]
  onElegir: (clave: string) => void
  hacia?: 'abajo' | 'arriba'
  alineado?: 'inicio' | 'fin'
  flotante?: boolean
}) {
  const [abierto, setAbierto] = useState(false)
  const [posicion, setPosicion] = useState<CSSProperties | undefined>(undefined)
  const contenedor = useRef<HTMLDivElement>(null)
  const disparadorRef = useRef<HTMLButtonElement>(null)
  const items = useRef<(HTMLButtonElement | null)[]>([])
  const idLista = useId()

  useEffect(() => {
    if (!abierto) return
    const fuera = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false)
    }
    document.addEventListener('mousedown', fuera)
    return () => document.removeEventListener('mousedown', fuera)
  }, [abierto])

  useLayoutEffect(() => {
    if (!flotante || !abierto || !disparadorRef.current) return setPosicion(undefined)
    const r = disparadorRef.current.getBoundingClientRect()
    const espacioAbajo = window.innerHeight - r.bottom
    const arriba = espacioAbajo < 240 && r.top > espacioAbajo
    const horizontal = alineado === 'fin' ? { right: window.innerWidth - r.right, left: 'auto' } : { left: r.left, right: 'auto' }
    const vertical = arriba ? { bottom: window.innerHeight - r.top + 6, top: 'auto' } : { top: r.bottom + 6, bottom: 'auto' }
    setPosicion({ position: 'fixed', zIndex: 60, ...horizontal, ...vertical })
    // Fijo a la ventana: si algo se desplaza, la lista quedaría suelta. Se cierra.
    const alDesplazar = () => setAbierto(false)
    window.addEventListener('scroll', alDesplazar, true)
    window.addEventListener('resize', alDesplazar)
    return () => {
      window.removeEventListener('scroll', alDesplazar, true)
      window.removeEventListener('resize', alDesplazar)
    }
  }, [abierto, flotante, alineado])

  const enfocar = (i: number) => {
    const n = opciones.length
    items.current[((i % n) + n) % n]?.focus()
  }

  const abrir = (foco: number) => {
    setAbierto(true)
    requestAnimationFrame(() => enfocar(foco))
  }

  const cerrar = (devolverFoco = true) => {
    setAbierto(false)
    if (devolverFoco) disparadorRef.current?.focus()
  }

  const tecladoDisparador = (e: KeyboardEvent) => {
    if (['ArrowDown', 'Enter', ' '].includes(e.key)) {
      e.preventDefault()
      abrir(0)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      abrir(opciones.length - 1)
    }
  }

  const tecladoLista = (e: KeyboardEvent) => {
    const actual = items.current.findIndex((el) => el === document.activeElement)
    if (e.key === 'ArrowDown') enfocar(actual + 1)
    else if (e.key === 'ArrowUp') enfocar(actual - 1)
    else if (e.key === 'Home') enfocar(0)
    else if (e.key === 'End') enfocar(opciones.length - 1)
    else if (e.key === 'Escape') cerrar()
    else if (e.key === 'Tab') return cerrar(false)
    else return
    e.preventDefault()
  }

  const grupos = [...new Set(opciones.map((o) => o.grupo ?? 0))]
  const clases = ['dcc-menu', hacia === 'arriba' && 'dcc-menu--up', alineado === 'fin' && 'dcc-menu--end', className].filter(Boolean).join(' ')

  return (
    <div className={clases} ref={contenedor}>
      {antes}
      <button
        ref={disparadorRef}
        type="button"
        className={`dcc-menu__trigger ${claseDisparador}`.trim()}
        aria-haspopup="menu"
        aria-expanded={abierto}
        aria-controls={idLista}
        aria-label={etiqueta}
        onClick={() => (abierto ? cerrar(false) : setAbierto(true))}
        onKeyDown={tecladoDisparador}
      >
        {disparador}
      </button>
      <div className="dcc-menu__list" role="menu" id={idLista} hidden={!abierto} onKeyDown={tecladoLista} style={posicion}>
        {grupos.map((grupo) => (
          <div className="dcc-menu__group" key={grupo}>
            {opciones.map((o, i) =>
              (o.grupo ?? 0) !== grupo ? null : (
                <button
                  key={o.clave}
                  ref={(el) => {
                    items.current[i] = el
                  }}
                  type="button"
                  className={`dcc-menu__item${o.peligro ? ' menu__item--peligro' : ''}`}
                  role={o.marcada !== undefined ? 'menuitemradio' : 'menuitem'}
                  aria-checked={o.marcada}
                  tabIndex={-1}
                  onClick={() => {
                    cerrar()
                    onElegir(o.clave)
                  }}
                >
                  {o.Icono && <o.Icono className="dcc-i" aria-hidden />}
                  {o.texto}
                </button>
              ),
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
